import { signUp } from "@/api/signUp";
import EnfiniShieldtLogo from "@/assets/images/shield-full-logo-color.png";
import BadgeCheckSVG from "@/assets/svg/badge-check.svg";
import YoutubeIconSVG from "@/assets/svg/youtube-icon.svg";
import EnfinityFooter from "@/components/enfinity/Footer";
import ChannelLinks from "@/components/signup/ChannelLinks";
import PersonalInformation from "@/components/signup/PersonalInformation";
import Sign from "@/components/signup/Sign";
import ToastModal from "@/components/ToastModal";
import { generateContractPdf } from "@/utils/contracts/contractTemplate";
import { shieldContractContent } from "@/utils/contracts/shieldContractContent";
import {
  alphabetChecker,
  isValidEmail,
  isValidUrl,
} from "@/utils/inputChecker";
import axios from "axios";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

export default function EnfiniShieldEsign() {
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    socials: {
      youtube: "",
      tiktok: "",
      facebook: "",
      instagram: "",
      twitchTv: "",
      douyin: "",
      kuaishou: "",
      rumble: "",
      others: "",
    },
    optIn: {
      msnDistribution: true,
      youtube: false,
      facebook: false,
      instagram: false,
      bilibili: false,
      tiktok: false,
      snapchatStars: false,
    },
    signature: "",
    tos: false,
    contentId: false,
    profitGuarantee: false,
    informationAccurate: false,
    service_type: {
      shield: "enfinishield",
      boost: "enfiniboost",
    },
  });

  const handleUserDataInput = (e) => {
    if (typeof e === "string") {
      setUserData((prev) => ({
        ...prev,
        country: e,
      }));
      return;
    }

    if (typeof e === "boolean") {
      setUserData((prev) => ({ ...prev, tos: e, partnerProgram: e }));
      return;
    }

    const { id, value, type, checked } = e.target;

    const sanitizedValue =
      ["firstName", "lastName"].includes(id) && type === "text"
        ? alphabetChecker(value)
        : value;

    setUserData((prev) => ({
      ...prev,
      [id]: type === "checkbox" ? checked : sanitizedValue,
    }));
  };

  const handleSocialChange = (e) => {
    const { id, value } = e.target;
    setUserData((prev) => ({
      ...prev,
      socials: {
        ...prev.socials,
        [id]: value,
      },
    }));
  };

  const handleOptInChange = (platformKey, checked) => {
    setUserData((prev) => ({
      ...prev,
      optIn: {
        ...prev.optIn,
        [platformKey]: checked,
      },
    }));
  };

  const signatureRef = useRef(null);
  const clearSignature = () => {
    signatureRef.current?.clear();
  };

  const undoSignature = () => {
    const data = signatureRef.current?.toData();
    if (data && data.length) {
      data.pop();
      signatureRef.current?.fromData(data);
    }
  };

  const handleEndStroke = () => {
    if (signatureRef.current) {
      const signatureData = signatureRef.current.toDataURL();
      setUserData((prev) => ({ ...prev, signature: signatureData }));
    }
  };

  const [ipAddress, setIpAddress] = useState("");
  useEffect(() => {
    const fetchIp = async () => {
      try {
        const response = await axios.get("https://api.ipify.org?format=json");
        setIpAddress(response.data.ip);
      } catch (error) {
        console.error("Error fetching IP address:", error);
      }
    };

    fetchIp();
  }, []);

  const [errorMessage, setErrorMessage] = useState("");
  const validateForm = () => {
    const {
      firstName,
      lastName,
      email,
      country,
      socials,
      signature,
      tos,
      informationAccurate,
    } = userData;

    if (!firstName) {
      setErrorMessage("First Name is required.");
      return false;
    }

    if (!lastName) {
      setErrorMessage("Last Name is required.");
      return false;
    }

    if (!email) {
      setErrorMessage("Email is required.");
      return false;
    }

    if (!isValidEmail(email)) {
      setErrorMessage("Please enter a valid email address.");
      return false;
    }

    if (!country) {
      setErrorMessage("Country is required.");
      return false;
    }

    const hasAtLeastOneLink = Object.values(socials).some(
      (link) => link.trim() !== ""
    );

    if (!hasAtLeastOneLink) {
      setErrorMessage("Please provide at least one social media link.");
      return false;
    }

    const invalidLinks = Object.entries(socials)
      .filter(([_, link]) => link.trim() !== "")
      .filter(([_, link]) => !isValidUrl(link));

    if (invalidLinks.length > 0) {
      const [platform] = invalidLinks[0];
      setErrorMessage(`Please enter a valid URL for ${platform}`);
      return false;
    }

    if (!signature) {
      setErrorMessage("Signature is required.");
      return false;
    }

    if (!tos) {
      setErrorMessage("You must accept the Terms of Service.");
      return false;
    }

    if (!informationAccurate) {
      setErrorMessage(
        "You must confirm your information is accurate to continue."
      );
      return false;
    }

    setErrorMessage("");
    return true;
  };

  const [successToast, setSuccessToast] = useState(false);
  const [toastType, setToastType] = useState(false);
  const [errorPrompt, setErrorPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSignUp = async () => {
    if (!validateForm()) return;

    if (!recaptchaToken) {
      setErrorPrompt("Please verify you're not a robot.");
      return;
    }

    const userDetails = {
      first_name: userData.firstName,
      last_name: userData.lastName,
      email: userData.email,
      country: userData.country,
      user_socials: {
        youtube: userData.socials.youtube ? userData.socials.youtube : "N/A",
        tiktok: userData.socials.tiktok ? userData.socials.tiktok : "N/A",
        twitch: userData.socials.twitchTv,
        instagram: userData.socials.instagram
          ? userData.socials.instagram
          : "N/A",
        facebook: userData.socials.facebook ? userData.socials.facebook : "N/A",
        douyin: userData.socials.douyin,
        kuaishou: userData.socials.kuaishou,
        rumble: userData.socials.rumble,
        others: userData.socials.others,
      },
      videos_protection_consent: userData.contentId,
      tos: userData.tos,
      information_accurate: userData.informationAccurate,
      profit_guarantee: userData.profitGuarantee,
      service_type: "enfinishield",
      dashboard_name: "enfinipay",
      optIn: {
        "MSN Distribution": userData.optIn.msnDistribution,
        YouTube: userData.optIn.youtube,
        Facebook: userData.optIn.facebook,
        Instagram: userData.optIn.instagram,
        Bilibili: userData.optIn.bilibili,
        TikTok: userData.optIn.tiktok,
        "Snapchat Stars": userData.optIn.snapchatStars,
      },
    };

    const file = await generateContractPdf(shieldContractContent, {
      signatureData: {
        user: userDetails,
        ipAddress,
        signature: userData.signature,
      },
      assets: { logo: EnfiniShieldtLogo },
      fileName: (ctx) =>
        `Enfinity_Limited_MSN_+_more_${ctx.user?.first_name}_${ctx.user?.last_name}_${ctx.user?.email}.pdf`,
    });

    const formData = new FormData();
    formData.append("first_name", userDetails.first_name);
    formData.append("last_name", userDetails.last_name);
    formData.append("email", userDetails.email);
    formData.append("country", userDetails.country);
    formData.append("service_type", userDetails.service_type);
    formData.append("user_socials", JSON.stringify(userDetails.user_socials));
    formData.append("opt_in", JSON.stringify(userDetails.optIn));
    formData.append("tos", userDetails.tos);
    formData.append(
      "videos_protection_consent",
      userDetails.videos_protection_consent
    );
    formData.append("profit_guarantee", userDetails.profit_guarantee);
    formData.append("information_accurate", userDetails.information_accurate);
    formData.append("dashboard_name", userDetails.dashboard_name);
    formData.append("contract_file", file);
    formData.append("recaptcha_token", recaptchaToken);

    // const pdfUrl = URL.createObjectURL(file);
    // window.open(pdfUrl);

    setIsLoading(true);

    try {
      const signUpResponse = await signUp(formData);

      if (signUpResponse.success) {
        setSuccessToast(true);
        setToastType("success");
        setRecaptchaToken("");
        setUserData({
          firstName: "",
          lastName: "",
          email: "",
          country: "",
          socials: {
            youtube: "",
            tiktok: "",
            facebook: "",
            instagram: "",
            twitchTv: "",
            douyin: "",
            kuaishou: "",
            rumble: "",
            others: "",
          },
          optIn: {
            msnDistribution: true,
            youtube: false,
            facebook: false,
            instagram: false,
            bilibili: false,
            tiktok: false,
            snapchatStars: false,
          },
          signature: "",
          tos: false,
          contentId: false,
          profitGuarantee: false,
          informationAccurate: false,
        });
      } else {
        setErrorPrompt(
          signUpResponse.message || "Something went wrong. Please try again."
        );
      }
    } catch (error) {
      console.error("Sign-up error:", error);
      setErrorPrompt(
        error.message || "Something went wrong. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const [showRest, setShowRest] = useState(false);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    const timer = setTimeout(() => setShowRest(true), 800);
    return () => clearTimeout(timer);
  }, []);

  // Staggered animation variants for smooth reveal
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.4,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 2,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const textVariants = {
    hidden: {
      opacity: 0,
      y: 40,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.5,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  // Letter variants for stagger animation
  const letterVariants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.2,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  // Container variants for staggering letters
  const headingSpanContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.02,
        delayChildren: 0.05,
      },
    },
  };

  return (
    <>
      {/* Header Section */}
      <div className="relative py-20 xxs:py-32 lg:py-40 4xl:py-[230px] px-4 xxs:px-8 xs:px-10 sm:px-10 md:px-10 lg:px-6 xl:px-8 4xl:px-16 flex item-center justify-center overflow-hidden">
        {/* Background Image */}
        <motion.div
          className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat z-[5] opacity-80"
          style={{
            backgroundImage: `url('https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/case-study-bg.png')`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 0.8, scale: 1 }}
          transition={{ duration: 3.5, ease: [0.22, 1, 0.36, 1] }}
        />
        {/* Background Video */}
        {/* <motion.video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-50 z-0"
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 0.5, scale: 1 }}
          transition={{ duration: 4, ease: [0.22, 1, 0.36, 1] }}
        >
          <source
            src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/WEBPAGE-nocard-1080p.mp4"
            type="video/mp4"
          />
        </motion.video> */}

        {/* Dark Overlay */}
        <motion.div
          className="absolute inset-0 w-full h-full bg-black/40 z-[2]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2.5, ease: [0.22, 1, 0.36, 1] }}
        ></motion.div>

        <motion.div
          className="mx-auto flex flex-col items-center gap-8 item-center justify-center relative z-10"
          variants={containerVariants}
          initial="visible"
          animate="visible"
        >
          {/* YouTube Badge - initial="visible" so badge always shows */}
          <motion.div
            className="flex justify-center items-center z-20 w-full"
            variants={itemVariants}
            initial="visible"
            animate="visible"
          >
            <a
              href="https://servicesdirectory.withyoutube.com/directory/enfinity-talent-agency/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="flex items-center gap-2 rounded-full py-2 px-5 border border-white/20 bg-white/5 backdrop-blur-md text-white shimmer-badge">
                <img
                  src={YoutubeIconSVG}
                  alt="Official YouTube Certified"
                  className="h-8 w-8"
                />
                <span className="xxs:text-xs xs:text-sm text-base">
                  Official YouTube Certified
                </span>
                <img src={BadgeCheckSVG} className="h-5 w-5" alt="Badge" />
              </div>
            </a>
          </motion.div>

          {/* Main Heading */}
          <motion.h1 className="text-5xl xxs:text-6xl xs:text-6xl sm:text-6xl md:text-6xl lg:text-[70px] 4xl:text-[90px] font-normal text-white text-center leading-[106px] xxs:leading-normal xs:leading-normal sm:leading-normal md:leading-normal lg:leading-[106px] enfinity-header-font">
            <motion.span
              className="block lg:inline-block"
              variants={headingSpanContainerVariants}
              initial="hidden"
              animate={showRest ? "visible" : "hidden"}
            >
              {"Get started with".split(" ").map((word, wordIndex) => (
                <span
                  key={`heading-word-${wordIndex}`}
                  style={{
                    display: "inline-block",
                    whiteSpace: "nowrap",
                    marginRight: "0.25em",
                  }}
                  className="text-white"
                >
                  {word.split("").map((char, charIndex) => (
                    <motion.span
                      key={`heading-${wordIndex}-${charIndex}`}
                      variants={letterVariants}
                      style={{ display: "inline-block" }}
                    >
                      {char}
                    </motion.span>
                  ))}
                </span>
              ))}
            </motion.span>
            <motion.span
              className="block lg:inline-block lg:ml-2 bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] bg-clip-text text-transparent"
              variants={headingSpanContainerVariants}
              initial="hidden"
              animate={showRest ? "visible" : "hidden"}
            >
              {"EnfiniShield".split("").map((char, charIndex) => (
                <motion.span
                  key={`shield-${charIndex}`}
                  variants={letterVariants}
                  style={{ display: "inline-block" }}
                >
                  {char}
                </motion.span>
              ))}
            </motion.span>
          </motion.h1>

          {/* Subtitle - initial="visible" so subtext always shows */}
          <motion.p
            className="text-2xl md:text-3xl lg:text-2xl font-normal text-white text-center max-w-[978px] leading-normal enfinity-subtext-font"
            variants={itemVariants}
            initial="visible"
            animate="visible"
          >
            Stop losing money to content thieves. We find every stolen video,
            file claims automatically, and get your money back, 24/7.
          </motion.p>
        </motion.div>
      </div>

      {/* Form Section */}
      <div className="space-y-12 pt-0 bg-white pb-20">
        <div className="flex flex-col justify-center items-center pb-16 lg:pb-48">
          <div className="flex flex-col justify-center items-center w-80 md:w-[30rem] xl:w-[50rem] z-40 text-black [&_h1]:text-black [&_p]:text-black [&_label]:text-black [&_span]:text-black">
            <PersonalInformation
              userData={userData}
              handleUserDataInput={handleUserDataInput}
              isEnfinity={true}
            />
            <ChannelLinks
              userData={userData}
              handleSocialChange={handleSocialChange}
              isEnfinity={true}
            />
            <Sign
              signatureRef={signatureRef}
              userData={userData}
              setUserData={setUserData}
              clearSignature={clearSignature}
              undoSignature={undoSignature}
              handleEndStroke={handleEndStroke}
              isLoading={isLoading}
              handleSignUp={handleSignUp}
              errorMessage={errorMessage}
              setRecaptchaToken={setRecaptchaToken}
              errorPrompt={errorPrompt}
              isEnfiniShield={true}
            />
          </div>
        </div>
        <EnfinityFooter />
      </div>

      <ToastModal
        showToast={successToast}
        setShowToast={setSuccessToast}
        form="esign"
        type={toastType}
      />
    </>
  );
}
